import React, { useMemo } from "react";
import { formatMoney } from "../../../../features/utils/formatMoney";

function ChangeDateConfirmationPriceBreakdown({ data }) {
  const travellerLib = {
    ADT: "Adult",
    CHD: "Child",
    CNN: "Child",
    INF: "Infant",
    INS: "Infant",
  };

  const passengers = data?.passengerInfo || [];
  const raw = data?.priceBreakdown || [];

  const perPassenger = useMemo(() => {
    return raw.map((it) => {
      const p = passengers.find((pp) => pp?.id === it?.passengerId) || {};
      const travellerType =
        it?.travellerType || travellerLib[p?.travelerType] || "Adult";
      const name =
        it?.passenger ||
        [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(" ") ||
        travellerType;

      const subtotal =
        (it.changePrice || 0) +
        (it.ticketDifference || 0) +
        (it.penaltyFee || 0) +
        (it.changeFee || 0);

      return { ...it, travellerType, passenger: name, subtotal };
    });
  }, [raw, passengers]);

  return (
    <div className="w-full border h-fit border-gray-200 rounded-xl overflow-hidden bg-white mt-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">Change</h2>
        <StatusBadge status={data?.status} />
      </div>

      {/* Content */}
      <div className="bg-primary1/5 p-4 sm:p-6 text-sm text-gray-700">
        {/* Per-passenger sections */}
        {perPassenger.map((row) => (
          <div key={row._id || row.passengerId} className="mb-4 last:mb-0">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium">1 x {row.travellerType}</span>
              <span className="font-semibold text-gray-900">
                {formatMoney(row.subtotal)}
              </span>
            </div>

            <div className="pl-4 space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">
                  Change fee per {row.travellerType.toLowerCase()}
                </span>
                <span className="text-gray-700">
                  {formatMoney(row.changePrice || 0)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">
                  Ticket difference per {row.travellerType.toLowerCase()}
                </span>
                <span className="text-gray-700">
                  {formatMoney(row.ticketDifference || 0)}
                </span>
              </div>

              {(row.penaltyFee || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">
                    Penalty fee per {row.travellerType.toLowerCase()}
                  </span>
                  <span className="text-gray-700">
                    {formatMoney(row.penaltyFee || 0)}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Service fee from top-level data */}
        <div className="flex justify-between items-center mt-4 mb-4">
          <span className="font-medium">
            {data?.passengerInfo?.length} x Service fee
          </span>
          <span className="font-semibold text-gray-900">
            {formatMoney(data?.changeFee || 0)}
          </span>
        </div>

        <hr className="border-gray-300 mb-3" />

        {/* Totals from top-level data */}
        <div className="flex justify-between items-center mb-1">
          <span className="font-semibold text-gray-900">Total</span>
          <span className="font-semibold text-gray-900">
            {formatMoney(data?.payable || 0)}
          </span>
        </div>

        <div className="text-right mt-1 text-primary1 font-semibold text-lg">
          {formatMoney(data?.payable || 0)}
        </div>
      </div>
    </div>
  );
}

export default ChangeDateConfirmationPriceBreakdown;

const StatusBadge = ({ status }) => {
  switch (status) {
    case "Pending":
      return (
        <span className="bg-[#FE9F29]/10 text-[#FE9F29] text-sm font-semibold px-3 py-1 rounded-md">
          Awaiting Assignment
        </span>
      );
    case "Approved":
      return (
        <span className="bg-[#FE9F29]/10 text-[#FE9F29] text-sm font-semibold px-3 py-1 rounded-md">
          Under Review by Admin
        </span>
      );

    case "ReIssued":
    case "Ticket Re-issued":
    case "Completed":
    case "Success":
      return (
        <span className="bg-green-100 text-green-700 text-sm font-semibold px-3 py-1 rounded-md">
          Ticket Re-issued
        </span>
      );
    case "Paid":
      return (
        <span className="bg-[#1E61DC]/10 text-[#1E61DC] text-sm font-semibold px-3 py-1 rounded-md">
          Paid
        </span>
      );

    case "Denied":
      return (
        <span className="bg-red-100 text-red-700 text-sm font-semibold px-3 py-1 rounded-md">
          Rejected
        </span>
      );
    case "Cancelled":
      return (
        <span className="bg-red-100 text-red-700 text-sm font-semibold px-3 py-1 rounded-md">
          Cancelled
        </span>
      );

    default:
      return (
        <span className="bg-gray-100 text-gray-600 text-sm font-semibold px-3 py-1 rounded-md">
          {status}
        </span>
      );
  }
};
